import type { Instant, UUID } from '@hoedokloop/domain';
import { CONTRACT_VERSION } from '@hoedokloop/contracts/versions';
import {
  classifyError,
  createErrorReference,
  createOperationalRecord,
  sanitizeDiagnosticTags,
} from '@hoedokloop/observability';
import {
  ANALYTICS_CONSENT_STORAGE_KEY,
  createConsentGatedAnalyticsTransport,
  createUnsupportedPlatformAdapters,
  type AnalyticsTransport,
  type CapabilityResult,
  type CapturedImage,
  type ErrorReport,
  type HapticPattern,
  type LifecycleState,
  type NotificationPermission,
  type PlatformAdapters,
  type ShareFile,
} from '@hoedokloop/platform-ports';
import { IndexedDbStudyRepository } from '@hoedokloop/storage-indexeddb/repository';

import { productChannel } from './channel';
import { createApiTransport } from './api-transport';

function now(): Instant {
  return new Date().toISOString() as Instant;
}

function success<Value>(value: Value): CapabilityResult<Value> {
  return { ok: true, value, observedAt: now() };
}

function failed<Value>(retryable = false): CapabilityResult<Value> {
  return { ok: false, reason: 'failed', retryable, observedAt: now() };
}

const acceptedImageTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
const maximumSourceBytes = 20 * 1024 * 1024;
const maximumStoredBytes = 2 * 1024 * 1024;
const maximumThumbnailBytes = 200 * 1024;
const maximumImageEdge = 2_048;
const thumbnailImageEdge = 480;

class UnsupportedImageError extends Error {}

function cancelledImage<Value>(): CapabilityResult<Value> {
  return { ok: false, reason: 'cancelled', retryable: false, observedAt: now() };
}

function unsupportedImage<Value>(): CapabilityResult<Value> {
  return { ok: false, reason: 'unsupported', retryable: false, observedAt: now() };
}

function scaledSize(width: number, height: number, maximumEdge: number) {
  const scale = Math.min(1, maximumEdge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

async function canvasBlob(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  maximumEdge: number,
  quality: number,
): Promise<{ blob: Blob; width: number; height: number }> {
  const { width, height } = scaledSize(sourceWidth, sourceHeight, maximumEdge);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new UnsupportedImageError('Canvas is unavailable');
  context.drawImage(source, 0, 0, width, height);
  const blob = await new Promise<Blob | undefined>((resolve) => {
    canvas.toBlob((value) => resolve(value ?? undefined), 'image/webp', quality);
  });
  if (!blob) throw new UnsupportedImageError('Image encoding is unavailable');
  return { blob, width, height };
}

async function encodeWithinBudget(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  maximumEdge: number,
  maximumBytes: number,
): Promise<{ blob: Blob; width: number; height: number }> {
  let edge = maximumEdge;
  while (edge >= 320) {
    for (const quality of [0.86, 0.74, 0.62]) {
      const encoded = await canvasBlob(source, sourceWidth, sourceHeight, edge, quality);
      if (encoded.blob.size <= maximumBytes) return encoded;
    }
    edge = Math.floor(edge * 0.8);
  }
  throw new UnsupportedImageError('Image cannot be compressed safely');
}

async function sha256(blob: Blob): Promise<string> {
  if (!crypto.subtle) throw new UnsupportedImageError('Secure image hashing is unavailable');
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Canvas-created Blob instances are not reliably structured-cloneable into
 * IndexedDB on WebKit. Re-wrapping their immutable bytes gives every channel
 * the same durable Blob representation before a repository transaction.
 */
async function durableBlob(blob: Blob): Promise<Blob> {
  return new Blob([await blob.arrayBuffer()], { type: blob.type });
}

async function withDecodedImage<Value>(
  blob: Blob,
  consume: (source: CanvasImageSource, width: number, height: number) => Promise<Value>,
): Promise<Value> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      try {
        return await consume(bitmap, bitmap.width, bitmap.height);
      } finally {
        bitmap.close();
      }
    } catch {
      // Some mobile browsers cannot decode HEIC through createImageBitmap.
      // The Image path below is a useful compatibility fallback.
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    image.decoding = 'async';
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new UnsupportedImageError('Unsupported image format'));
      image.src = url;
    });
    if (image.naturalWidth < 1 || image.naturalHeight < 1) {
      throw new UnsupportedImageError('Invalid image dimensions');
    }
    return await consume(image, image.naturalWidth, image.naturalHeight);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function normalizeCapturedImage(blob: Blob): Promise<CapturedImage> {
  if (!acceptedImageTypes.has(blob.type)) {
    throw new UnsupportedImageError('Unsupported image type');
  }
  if (blob.size < 1 || blob.size > maximumSourceBytes) {
    throw new UnsupportedImageError('Image is outside the supported size');
  }
  return withDecodedImage(blob, async (source, width, height) => {
    const original = await encodeWithinBudget(
      source,
      width,
      height,
      maximumImageEdge,
      maximumStoredBytes,
    );
    const thumbnail = await encodeWithinBudget(
      source,
      width,
      height,
      thumbnailImageEdge,
      maximumThumbnailBytes,
    );
    if (!acceptedImageTypes.has(original.blob.type) || original.blob.type !== thumbnail.blob.type) {
      throw new UnsupportedImageError('Image encoder returned an unsupported format');
    }
    const [durableOriginal, durableThumbnail] = await Promise.all([
      durableBlob(original.blob),
      durableBlob(thumbnail.blob),
    ]);
    return {
      original: durableOriginal,
      thumbnail: durableThumbnail,
      // Safari may fall back to PNG even when a WebP encode was requested.
      // Persist the actual normalized type so repository integrity checks stay true.
      mimeType: original.blob.type,
      width: original.width,
      height: original.height,
      byteLength: durableOriginal.size,
      sha256: await sha256(durableOriginal),
      capturedAt: now(),
    };
  });
}

function pickImage(source: 'camera' | 'album'): Promise<CapabilityResult<CapturedImage[]>> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = [...acceptedImageTypes].join(',');
    input.multiple = source === 'album';
    input.hidden = true;
    if (source === 'camera') input.setAttribute('capture', 'environment');

    let settled = false;
    const finish = (result: CapabilityResult<CapturedImage[]>) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(result);
    };

    input.addEventListener('cancel', () => finish(cancelledImage()));
    input.addEventListener('change', () => {
      void (async () => {
        const files = Array.from(input.files ?? []);
        if (files.length === 0) {
          finish(cancelledImage());
          return;
        }
        try {
          const images = await Promise.all(files.map((file) => normalizeCapturedImage(file)));
          finish({ ok: true, value: images, observedAt: now() });
        } catch (error) {
          finish(error instanceof UnsupportedImageError ? unsupportedImage() : failed(true));
        }
      })();
    });
    document.body.append(input);
    input.click();
  });
}

function hasSameOriginAnalyticsEndpoint(): boolean {
  return !['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
}

function saveFile(file: ShareFile): void {
  const blob = new Blob([file.bytes as BlobPart], { type: file.type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.name;
  anchor.click();
  URL.revokeObjectURL(url);
}

const hapticDurations: Record<HapticPattern, number | number[]> = {
  light: 10,
  medium: 20,
  success: [10, 40, 20],
};

function triggerWebHaptic(pattern: HapticPattern): boolean {
  try {
    return typeof navigator.vibrate === 'function' && navigator.vibrate(hapticDurations[pattern]);
  } catch {
    return false;
  }
}

export function createWebPlatformAdapters(
  channel: 'web' | 'ait' = productChannel,
): PlatformAdapters {
  const api = createApiTransport(channel);
  const unsupported = createUnsupportedPlatformAdapters(now);
  const studyRepository = new IndexedDbStudyRepository({ name: 'hoedokloop', now });
  const settingKey = (key: string) => `secure:${key}`;
  const readSetting = async <Value>(key: string): Promise<Value | undefined> => {
    await studyRepository.open();
    return (await studyRepository.database.settings.get(settingKey(key)))?.value as
      Value | undefined;
  };
  const writeSetting = async (key: string, value: unknown): Promise<void> => {
    await studyRepository.open();
    await studyRepository.database.settings.put({ key: settingKey(key), value, updatedAt: now() });
  };
  const installationId = async (): Promise<UUID> => {
    const existing = await readSetting<string>('installation-id');
    if (existing && /^[0-9a-f-]{36}$/iu.test(existing)) return existing as UUID;
    const created = crypto.randomUUID() as UUID;
    await writeSetting('installation-id', created);
    return created;
  };

  const persistDeviceCredential = async (value: unknown): Promise<void> =>
    writeSetting('device-credential', value);

  const deviceRequest = async (path: string, init: RequestInit) => {
    const response = await api.request(path, init);
    if (!response.ok) throw new Error('Device credential request failed');
    return response.json() as Promise<{ deviceId: UUID; credential: string }>;
  };

  const analyticsDelegate: Pick<AnalyticsTransport, 'send'> = {
    send: async (events) => {
      if (events.length === 0) return success({ accepted: 0 });
      if (!hasSameOriginAnalyticsEndpoint()) return failed();
      if (!navigator.onLine) return failed(true);
      try {
        const response = await api.request('/v1/events', {
          method: 'POST',
          headers: { 'content-type': 'application/json', 'x-hoedokloop-channel': channel },
          body: JSON.stringify({ events }),
          keepalive: true,
        });
        if (!response.ok) return failed(response.status >= 500 || response.status === 429);
        const payload = (await response.json()) as { accepted?: unknown };
        return typeof payload.accepted === 'number'
          ? success({ accepted: payload.accepted })
          : failed();
      } catch {
        return failed(true);
      }
    },
  };
  const analytics = createConsentGatedAnalyticsTransport({
    delegate: analyticsDelegate,
    store: {
      read: () => window.localStorage.getItem(ANALYTICS_CONSENT_STORAGE_KEY),
      write: (choice) => window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, choice),
    },
    now,
  });

  return {
    studyCapabilities: { undatedExamCreation: channel === 'web' },
    ...unsupported,
    studyRepository,
    api,
    identity: {
      getInstallationId: async () => success(await installationId()),
      getPlatformSubject: unsupported.identity.getPlatformSubject,
      getDeviceCredential: async () => {
        try {
          return success(
            await readSetting<{ deviceId: UUID; credential: string }>('device-credential'),
          );
        } catch {
          return failed(true);
        }
      },
      issueDeviceCredential: async (challengeToken) => {
        try {
          const value = await deviceRequest('/v1/devices', {
            method: 'POST',
            headers: { 'content-type': 'application/json', 'x-hoedokloop-channel': channel },
            body: JSON.stringify({ platform: channel, turnstileToken: challengeToken }),
          });
          await persistDeviceCredential(value);
          return success(value);
        } catch {
          return failed(true);
        }
      },
      rotateDeviceCredential: async () => {
        try {
          const current = await readSetting<{ deviceId: UUID; credential: string }>(
            'device-credential',
          );
          if (!current) return failed();
          const value = await deviceRequest('/v1/devices/rotate', {
            method: 'POST',
            headers: {
              authorization: `Device ${current.credential}`,
              'x-hoedokloop-channel': channel,
            },
          });
          await persistDeviceCredential(value);
          return success(value);
        } catch {
          return failed(true);
        }
      },
      revokeDeviceCredential: async () => {
        try {
          const current = await readSetting<{ credential: string }>('device-credential');
          if (current) {
            const response = await api.request('/v1/devices/current', {
              method: 'DELETE',
              headers: {
                authorization: `Device ${current.credential}`,
                'x-hoedokloop-channel': channel,
              },
            });
            if (!response.ok) return failed(response.status >= 500);
          }
          await studyRepository.database.settings.delete(settingKey('device-credential'));
          return success(undefined);
        } catch {
          return failed(true);
        }
      },
    },
    notifications: {
      permission: async () =>
        success<NotificationPermission>(
          typeof Notification === 'undefined'
            ? 'unsupported'
            : Notification.permission === 'default'
              ? 'prompt'
              : Notification.permission,
        ),
      requestPermission: async () => {
        if (typeof Notification === 'undefined')
          return unsupported.notifications.requestPermission();
        try {
          const permission = await Notification.requestPermission();
          return success(permission === 'default' ? 'prompt' : permission);
        } catch {
          return failed();
        }
      },
      subscribe: async () => {
        const publicKey = import.meta.env['VITE_WEB_PUSH_PUBLIC_KEY'];
        if (!('serviceWorker' in navigator) || !publicKey)
          return unsupported.notifications.subscribe();
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: publicKey,
          });
          const serialized = subscription.toJSON();
          const p256dh = serialized.keys?.['p256dh'];
          const auth = serialized.keys?.['auth'];
          if (!p256dh || !auth) return failed();
          const digest = await crypto.subtle.digest(
            'SHA-256',
            new TextEncoder().encode(subscription.endpoint),
          );
          const id = [...new Uint8Array(digest)]
            .map((byte) => byte.toString(16).padStart(2, '0'))
            .join('');
          return success({
            id,
            endpoint: subscription.endpoint,
            p256dh,
            auth,
            ...(subscription.expirationTime
              ? { expiresAt: new Date(subscription.expirationTime).toISOString() as Instant }
              : {}),
          });
        } catch {
          return failed(true);
        }
      },
      unsubscribe: async () => {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) await subscription.unsubscribe();
          return success(undefined);
        } catch {
          return failed(true);
        }
      },
    },
    navigation: {
      back: () => {
        window.history.back();
        return success(undefined);
      },
      close: unsupported.navigation.close,
      open: (path) => {
        if (!path.startsWith('/')) return failed();
        window.location.assign(path);
        return success(undefined);
      },
      openExternal: (url) => {
        try {
          const parsed = new URL(url);
          if (!['https:', 'http:'].includes(parsed.protocol)) return failed();
          const opened = window.open(parsed, '_blank', 'noopener,noreferrer');
          return opened ? success(undefined) : failed();
        } catch {
          return failed();
        }
      },
    },
    lifecycle: {
      current: () => success<LifecycleState>(document.hidden ? 'background' : 'foreground'),
      isOnline: () => success(navigator.onLine),
      subscribeState: (listener) => {
        const handler = () => listener(document.hidden ? 'background' : 'foreground');
        document.addEventListener('visibilitychange', handler);
        return success(() => document.removeEventListener('visibilitychange', handler));
      },
      subscribeOnline: (listener) => {
        const online = () => listener(true);
        const offline = () => listener(false);
        window.addEventListener('online', online);
        window.addEventListener('offline', offline);
        return success(() => {
          window.removeEventListener('online', online);
          window.removeEventListener('offline', offline);
        });
      },
    },
    camera: {
      permission: async (source) => {
        if (source === 'album' || !navigator.permissions) return success('prompt');
        try {
          const permission = await navigator.permissions.query({
            name: 'camera' as PermissionName,
          });
          return success(
            permission.state === 'granted'
              ? 'granted'
              : permission.state === 'denied'
                ? 'denied'
                : 'prompt',
          );
        } catch {
          return success('prompt');
        }
      },
      async capture() {
        const result = await pickImage('camera');
        return result.ok
          ? result.value[0]
            ? { ok: true, value: result.value[0], observedAt: result.observedAt }
            : cancelledImage<CapturedImage>()
          : result;
      },
      selectFromAlbum: () => pickImage('album'),
    },
    share: {
      createLink: async (path) =>
        channel !== 'web'
          ? ({ ok: false, reason: 'unsupported', retryable: false, observedAt: now() } as const)
          : success(`https://hoedokloop.com${path.startsWith('/') ? path : '/'}?from=share_card`),
      copyText: async (text) => {
        if (!navigator.clipboard?.writeText)
          return { ok: false, reason: 'unsupported', retryable: false, observedAt: now() };
        try {
          await navigator.clipboard.writeText(text);
          return success(undefined);
        } catch {
          return failed(true);
        }
      },
      share: async (payload) => {
        if (!navigator.share) return unsupported.share.share(payload);
        try {
          const files = payload.files?.map(
            (file) => new File([file.bytes as BlobPart], file.name, { type: file.type }),
          );
          if (files && navigator.canShare && !navigator.canShare({ files }))
            return { ok: false, reason: 'unsupported', retryable: false, observedAt: now() };
          await navigator.share({
            title: payload.title,
            ...(payload.text === undefined ? {} : { text: payload.text }),
            ...(payload.url === undefined ? {} : { url: payload.url }),
            ...(files === undefined ? {} : { files }),
          });
          return success(undefined);
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return { ok: false, reason: 'cancelled', retryable: false, observedAt: now() };
          }
          return failed(true);
        }
      },
      save: async (file) => {
        saveFile(file);
        return success(undefined);
      },
    },
    secureStorage: {
      get: async (key) => {
        try {
          const value = await readSetting<number[]>(key);
          return success(value === undefined ? undefined : Uint8Array.from(value));
        } catch {
          return failed(true);
        }
      },
      set: async (key, value) => {
        try {
          await writeSetting(key, [...value]);
          return success(undefined);
        } catch {
          return failed(true);
        }
      },
      remove: async (key) => {
        try {
          await studyRepository.database.settings.delete(settingKey(key));
          return success(undefined);
        } catch {
          return failed(true);
        }
      },
    },
    analytics,
    errors: {
      capture: (error, tags) => {
        const referenceId = createErrorReference();
        const report: ErrorReport = { referenceId, capturedAt: now() };
        if (import.meta.env.DEV) {
          console.error('hoedokloop_client_error', {
            record: createOperationalRecord({
              event: 'client_error',
              severity: 'error',
              occurredAt: report.capturedAt,
              requestId: crypto.randomUUID(),
              release: {
                channel: channel,
                buildVersion: import.meta.env['VITE_BUILD_VERSION'] ?? 'local',
                contractVersion: CONTRACT_VERSION,
              },
              route: window.location.pathname,
              errorCode: classifyError(error),
              referenceId,
            }),
            tags: sanitizeDiagnosticTags(tags),
          });
        }
        return success(report);
      },
    },
    haptics: {
      trigger: (pattern) =>
        triggerWebHaptic(pattern) ? success(undefined) : unsupported.haptics.trigger(pattern),
    },
  };
}
